import { useRef, useMemo, useState, useCallback } from "react";
import ThreeDotsVerticalIcon from "../../../../../../../../../../../assets/svg/three-dots-vertical-icon";
import ReplyIcon from "../../../../../../../../../../../assets/svg/reply-icon";
import SimpleDocumentIcon from "../../../../../../../../../../../assets/svg/simple-document-icon";
import { TicketCommentInput } from "./TicketCommentInput";
import { CommentAuthor } from "./CommentAuthor";
import { CommentActionMenu } from "./CommentActionMenu";
import { type TicketComment, type TicketCommentCreatedBy } from "./CommentCard";
import { type AttachmentPreviewItem } from "../../../../../../../../../../Communications/utils/types";
import { getDateTimeByFormat } from "../../../../../../../../../../../redux/slices/dateTimeFormatSlice";
import { processHtmlContent } from "../../../../../../../../../../../utils/functions/formatters";
import EmailBodyIframe from "./EmailBodyIframe";

const resolveCreatedByName = (
  createdBy: TicketCommentCreatedBy | undefined
): string | undefined => {
  if (typeof createdBy === "object") return createdBy?.name ?? "Unknown";
  return "Unknown";
};

const getUniqueRepliers = (replies: TicketComment[]) => {
  const seen = new Set<string>();
  return replies?.reduce<TicketCommentCreatedBy[]>((acc, reply) => {
    const name = resolveCreatedByName(reply?.created_by) ?? "";
    if (!seen?.has(name)) {
      seen?.add(name);
      acc?.push(reply?.created_by);
    }
    return acc;
  }, []);
};

const COLLAPSED_MAX_HEIGHT = 68;

interface ActivityCommentItemProps {
  comment: TicketComment;
  showReplyButton?: boolean;
  showRepliesBar?: boolean;
  isExpanded: boolean;
  isOverflowing: boolean;
  isEditing: boolean;
  isMenuOpen: boolean;
  commentImages: AttachmentPreviewItem[];
  containerClassName?: string;
  hideEdit?: boolean;
  canUpdate?: boolean;
  isOwnComment?: boolean;
  contentRef: (el: HTMLDivElement | null) => void;
  onToggleExpansion: () => void;
  onReply: () => void;
  onEditCancel: () => void;
  onEditStart: () => void;
  onDeleteRequest: () => void;
  onMenuToggle: () => void;
  onMenuClose: () => void;
  onAttachmentPreview: (index: number) => void;
}

const ActivityCommentItem = ({
  comment,
  showReplyButton = true,
  showRepliesBar = true,
  isExpanded,
  isOverflowing,
  isEditing,
  isMenuOpen,
  commentImages,
  containerClassName = "px-3 py-5",
  hideEdit,
  canUpdate = true,
  isOwnComment = false,
  contentRef,
  onToggleExpansion,
  onReply,
  onEditCancel,
  onEditStart,
  onDeleteRequest,
  onMenuToggle,
  onMenuClose,
  onAttachmentPreview,
}: ActivityCommentItemProps) => {
  const menuBtnRef = useRef<HTMLButtonElement>(null);

  const contentIsHtml = useMemo(() => {
    const trimmed = (comment?.content ?? "").trim();
    return trimmed.startsWith("<html") && trimmed.endsWith("</html>");
  }, [comment?.content]);

  const processedHtmlContent = useMemo(() => {
    if (!contentIsHtml || !comment?.content) return "";
    return processHtmlContent(comment.content);
  }, [contentIsHtml, comment?.content]);

  const [iframeOverflowing, setIframeOverflowing] = useState(false);
  const [iframeExpanded, setIframeExpanded] = useState(false);

  const handleIframeOverflowChange = useCallback((overflowing: boolean) => {
    setIframeOverflowing(overflowing);
  }, []);

  const handleIframeToggle = () => {
    setIframeExpanded((prev) => !prev);
  };

  const getContentHtml = () => {
    if (!isExpanded) return comment?.content;
    const html = comment?.content ?? "";
    const btn =
      ' <span data-read-less class="text-verba-blue dark:text-verba-blue-dk font-medium cursor-pointer hover:underline">... read less</span>';
    const injected = html?.replace(/<\/(p|div|li)>\s*$/, `${btn}</$1>`);
    return injected !== html ? injected : html + btn;
  };

  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target?.closest("[data-read-less]")) {
      onToggleExpansion();
      return;
    }
    if (target.tagName === "IMG") {
      e?.preventDefault();
      const src = (target as HTMLImageElement).src;
      const idx = commentImages?.findIndex((a) => a.s3_url === src);
      if (idx !== -1) onAttachmentPreview(idx);
    }
  };

  return (
    <div className="border-b border-border-gray dark:border-border-gray-dk">
      <div
        className={`grid transition-all duration-500 ease-in-out ${
          isEditing
            ? "grid-rows-[1fr] opacity-100"
            : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          {isEditing && (
            <TicketCommentInput
              mode="edit"
              editCommentId={comment?.id}
              initialContent={comment?.content}
              initialAttachments={comment?.attachments}
              onCancel={onEditCancel}
              onEditSuccess={onEditCancel}
            />
          )}
        </div>
      </div>

      <div
        className={`${containerClassName} flex flex-col gap-3 ${isEditing ? "hidden" : ""}`}
      >
        {contentIsHtml ? (
          <div className="text-md font-normal leading-normal">
            <div className="relative overflow-x-auto">
              <EmailBodyIframe
                html={processedHtmlContent}
                maxCollapsedHeight={COLLAPSED_MAX_HEIGHT}
                isExpanded={iframeExpanded}
                onOverflowChange={handleIframeOverflowChange}
              />
            </div>
            {iframeOverflowing && (
              <p className="cursor-pointer mt-1" onClick={handleIframeToggle}>
                <span className="text-md text-verba-blue dark:text-verba-blue-dk font-medium hover:underline">
                  {iframeExpanded ? "read less" : "...read more"}
                </span>
              </p>
            )}
          </div>
        ) : (
          <div className="text-md font-normal text-heading-text dark:text-heading-text-dk leading-normal relative">
            <div
              ref={contentRef}
              className={`ticket-comment-content ${!isExpanded ? "line-clamp-3" : ""}`}
              dangerouslySetInnerHTML={{ __html: getContentHtml() }}
              onClick={handleContentClick}
            />
            {!isExpanded && isOverflowing && (
              <span
                className="absolute bottom-0 right-0 bg-pure-white dark:bg-pure-white-dk cursor-pointer"
                onClick={onToggleExpansion}
              >
                <span className="text-heading-text dark:text-heading-text-dk">
                  ...{" "}
                </span>
                <span className="text-verba-blue dark:text-verba-blue-dk font-medium hover:underline">
                  read more
                </span>
              </span>
            )}
          </div>
        )}

        {comment?.attachments?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {comment?.attachments?.map((att) => {
              const isImage = att?.content_type?.startsWith("image/");
              if (isImage) {
                const imageIdx = commentImages?.findIndex(
                  (a) => a.s3_url === att.s3_url
                );
                return (
                  <button
                    key={att?.s3_url}
                    type="button"
                    onClick={() =>
                      onAttachmentPreview(imageIdx !== -1 ? imageIdx : 0)
                    }
                    className="block rounded-lg overflow-hidden border border-border-gray dark:border-border-gray-dk w-full hover:opacity-90 transition-opacity cursor-pointer"
                  >
                    <img
                      src={att?.s3_url}
                      alt={att?.file_name}
                      className="w-full h-auto object-contain"
                    />
                  </button>
                );
              }
              return (
                <a
                  key={att?.s3_url}
                  href={att?.s3_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-border-gray dark:border-border-gray-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors max-w-[200px]"
                >
                  <SimpleDocumentIcon className="w-[18px] h-[18px] shrink-0" />
                  <span className="text-xs font-medium text-heading-text dark:text-heading-text-dk truncate">
                    {att?.file_name}
                  </span>
                </a>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between gap-[12px]">
          <div className="flex items-center gap-[5px] flex-1 min-w-0">
            <CommentAuthor
              userName={resolveCreatedByName(comment?.created_by)}
            />
          </div>
          <div
            className="relative flex items-center gap-[5px] shrink-0"
            data-comment-menu
          >
            <p className="text-xs font-medium text-gray-placeholder dark:text-gray-placeholder-dk whitespace-nowrap">
              {getDateTimeByFormat(new Date(comment?.created_at))?.date},{" "}
              {getDateTimeByFormat(new Date(comment?.created_at))?.time}
            </p>
            {showReplyButton &&
              !(showRepliesBar && comment?.replies?.length) && (
                <>
                  <span className="text-border-gray dark:text-border-gray-dk">
                    |
                  </span>
                  <button
                    className="flex items-center gap-0.5 cursor-pointer hover:opacity-80"
                    onClick={onReply}
                  >
                    <ReplyIcon className="w-[14px] h-[14px] text-verba-indigo dark:text-verba-indigo-dk" />
                    <span className="text-xs font-medium text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
                      Reply
                    </span>
                  </button>
                </>
              )}
            {isOwnComment && (
              <>
                <button
                  data-note-kibana-btn
                  ref={menuBtnRef}
                  onClick={onMenuToggle}
                >
                  <ThreeDotsVerticalIcon className="w-[14px] h-[14px] text-gray-placeholder dark:text-gray-placeholder-dk" />
                </button>
                {isMenuOpen && (
                  <CommentActionMenu
                    triggerRef={menuBtnRef}
                    hideEdit={hideEdit}
                    disabled={!canUpdate}
                    onEdit={onEditStart}
                    onDelete={onDeleteRequest}
                    onClose={onMenuClose}
                  />
                )}
              </>
            )}
          </div>
        </div>

        {showRepliesBar && comment?.replies && comment?.replies?.length > 0 && (
          <button
            className="flex items-center justify-center gap-1 w-full bg-hover-effect dark:bg-hover-effect-dk rounded-lg px-2 py-1 cursor-pointer"
            onClick={onReply}
          >
            <ReplyIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk shrink-0" />
            <p className="text-xs font-medium text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
              {comment?.replies?.length}{" "}
              {comment?.replies?.length === 1 ? "Reply" : "Replies"}
            </p>
            <div className="flex items-center pr-[6px] shrink-0">
              {getUniqueRepliers(comment?.replies)
                .slice(0, 3)
                .map((createdBy, index) => (
                  <div
                    key={resolveCreatedByName(createdBy) ?? index}
                    className="relative shrink-0"
                    style={{
                      marginLeft: index > 0 ? "-6px" : 0,
                      zIndex: 3 - index,
                    }}
                  >
                    <CommentAuthor
                      userName={resolveCreatedByName(createdBy)}
                      showName={false}
                    />
                  </div>
                ))}
            </div>
          </button>
        )}
      </div>
    </div>
  );
};

export default ActivityCommentItem;
